/**
 * Internationalization helpers.
 * - Dictionary-based UI strings.
 * - For backend content stored as { de, en, fr } objects, use pick().
 */

const SUPPORTED = ["de", "en", "fr"];
const DEFAULT_LANG = "de";

export function detectInitialLang() {
  // 1) URL prefix
  const seg = window.location.pathname.split("/").filter(Boolean)[0];
  if (SUPPORTED.includes(seg)) return seg;
  // 2) localStorage
  const stored = localStorage.getItem("kb_lang");
  if (SUPPORTED.includes(stored)) return stored;
  // 3) browser
  const nav = (navigator.language || "de").slice(0, 2).toLowerCase();
  if (SUPPORTED.includes(nav)) return nav;
  // 4) fallback
  return DEFAULT_LANG;
}

/**
 * Pick localized value from a multilingual field.
 * Supports either string (returned as-is) or {de, en, fr} object.
 */
export function pick(field, lang = DEFAULT_LANG) {
  if (field == null) return "";
  if (typeof field === "string") return field;
  if (typeof field === "object") {
    return field[lang] || field.de || field.en || field.fr || "";
  }
  return String(field);
}

/** Strip the leading lang segment from a pathname, returning the language-agnostic path. */
export function stripLangPrefix(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length && SUPPORTED.includes(parts[0])) {
    return "/" + parts.slice(1).join("/");
  }
  return pathname === "" ? "/" : pathname;
}

/** Build a URL with the given language prefix. DE has no prefix. */
export function withLang(pathname, lang) {
  const clean = stripLangPrefix(pathname) || "/";
  if (lang === "de" || !lang) {
    return clean === "/" ? "/" : clean;
  }
  return clean === "/" ? `/${lang}` : `/${lang}${clean}`;
}

export { SUPPORTED, DEFAULT_LANG };

// ----------------------------------------------------------------------------
// UI Dictionaries
// ----------------------------------------------------------------------------
export const DICT = {
  de: {
    seo: {
      home: {
        title: "Karim Benna – Freelance IT-Consultant für Backend, QA & KI",
        description: "Freiberuflicher IT-Consultant mit über 9 Jahren Erfahrung in Java/Spring Boot, QA, Product Ownership und Applied AI. Eingesetzt bei Audi, BMW und CARIAD.",
      },
      about: {
        title: "Über Karim Benna",
        description: "Senior IT-Consultant, MIT-zertifiziert in Applied AI & Data Science, viersprachig (DE, EN, FR, AR) – mit Sitz in Deutschland und Portugal.",
      },
      services: {
        title: "Leistungen: Backend, QA, KI-Beratung & Product Ownership",
        description: "Sechs Kernbereiche: Java/Spring Boot Backend, JavaScript/TypeScript/GraphQL, React Native, ISTQB-zertifizierte QA, Product Ownership und KI-Beratung.",
      },
      experience: {
        title: "Werdegang & Lebenslauf",
        description: "12 Stationen seit 2012 – von Online-Marketing über Frontend & QA bis zu Backend-Microservices und Product Ownership bei Audi, BMW und CARIAD.",
      },
      blog: {
        title: "Blog – IT-Consulting, Backend, QA & KI",
        description: "Artikel zu Java/Spring Boot, JavaScript/TypeScript, ISTQB-QA, Product Ownership und Applied AI aus der Praxis eines Senior IT-Consultants.",
      },
      contact: {
        title: "Kontakt",
        description: "Schreib mir – freiberufliche IT-Consulting-Anfragen, Projekt-Discovery oder Sparring. Antwort in der Regel innerhalb von 48 Stunden.",
      },
    },
    projects: {
      label: "Ausgewählte AI/Data-Projekte",
      title: "Echte",
      titleAccent: "AI-Projekte",
      titleEnd: ", die ich gebaut habe.",
      intro: "Drei Capstone- und Kursprojekte aus dem MIT Applied AI & Data Science Programm – von Generative-AI-Systemen über Deep-Learning-Bilderkennung bis zur explorativen Datenanalyse.",
      programLabel: "Programm",
      programName: "MIT Applied AI & Data Science",
      viewLink: "Projekt ansehen",
      fullPortfolio: "Vollständiges Portfolio auf Great Learning ansehen",
      items: {
        "retail-genai": {
          course: "Capstone · Generative AI",
          title: "Real-Time Retail Feedback Intelligence",
          desc: "Generative-AI-System für 'ChicStyle', das Tausende Kundenbewertungen in Echtzeit analysiert. Mittels Zero-Shot-, Few-Shot- und Chain-of-Thought-Prompting werden Stimmung, betroffenes Produkt und dringende Probleme erkannt – plus automatisch generierte, personalisierte Kundenantworten für ein schnelleres Reaktionsteam.",
        },
        "svhn-cnn": {
          course: "Elective · Deep Learning",
          title: "Street-View Housing Number – Digit Recognition",
          desc: "Deep-Learning-Lösung zur Erkennung von Ziffern aus realen Straßenbildern (SVHN-Datensatz, .h5). Verschiedene ANN- und CNN-Architekturen wurden trainiert, verglichen und evaluiert, um das beste Modell für robuste Digit-Vorhersagen auszuwählen.",
        },
        "foodhub-eda": {
          course: "Foundations of AI",
          title: "FoodHub – Order Analysis",
          desc: "Explorative Datenanalyse für ein Food-Aggregator-Unternehmen: Bestellverhalten der Kunden, Restaurant-Performance und Trends wurden mit NumPy/Pandas und Visualisierungen untersucht, um datengetriebene Verbesserungen im Kundenerlebnis abzuleiten.",
        },
      },
    },
    common: {
      bookCall: "Kontakt aufnehmen",
      bookFree: "Kontakt aufnehmen",
      seeServices: "Leistungen ansehen",
      sendMessage: "Nachricht senden",
      contactMe: "Nachricht senden",
      moreAbout: "Mehr über mich",
      readMore: "Weiterlesen",
      readArticle: "Lesen",
      allArticles: "Alle Beiträge",
      cvPdf: "Lebenslauf als PDF",
      bookAppointment: "Kontakt aufnehmen",
      preferEmail: "Per E-Mail anfragen",
      responseTime: "Antwortzeit",
      languageLabel: "Sprache wechseln",
      learnMore: "Mehr erfahren",
      requestProject: "Projekt anfragen",
      backToBlog: "Alle Beiträge",
      backHome: "Zurück zur Startseite",
      seeMatch: "Match",
      sendButton: "Senden",
      loading: "Laden…",
      saving: "Sende...",
    },
    nav: {
      home: "Start",
      about: "Wer ich bin",
      services: "Leistungen",
      experience: "Werdegang",
      blog: "Insights",
      contact: "Kontakt",
    },
    hero: {
      pillAvailable: "Aktuell Anfragen für Q3/2026 möglich",
      titleA: "Dein Senior-",
      titleAccent: "IT-Consultant",
      titleB: "für komplexe Backend- &",
      titleC: "Quality-Projekte.",
      intro:
        "Ich bin <strong>Karim Benna</strong>, freiberuflicher IT-Consultant für Backend-Entwicklung, Qualitätssicherung und technische Projektkoordination. Seit 2017 unterstütze ich <gold>Enterprise-Projekte</gold> dabei, komplexe Softwarelösungen stabil, verständlich und zukunftsfähig umzusetzen.",
      tagline: "Softwareentwicklung, Qualitätssicherung und technische Koordination aus einer Hand.",
      stats: {
        yearsExp: "Jahre Erfahrung",
        stations: "Stationen",
        certs: "Zertifikate (ISTQB · MIT)",
      },
      availability: "Verfügbarkeit",
      mode: "Modus",
      location: "Standort",
      clientStrip: "Im Einsatz bei",
      responseShort: "Üblicherweise < 48 Std.",
      langChips: "Deutsch · English · Français",
    },
    quick: {
      label: "Schnellzugriff",
      title: "Was dich hier",
      titleAccent: "erwartet",
      titleEnd: ".",
      tools: { label: "Tech-Stack", title: "Tools im Detail" },
      certs: { label: "Zertifikate", title: "Zertifikate herunterladen" },
      experience: { label: "Werdegang", title: "Liste der Stationen" },
      services: { label: "Leistungen", title: "Kompetenzbereiche" },
    },
    why: {
      label: "Warum mit mir",
      title: "Was du von der Zusammenarbeit",
      titleAccent: "erwarten kannst",
      desc: "Ich unterstütze dich bei der Weiterentwicklung deines Projekts mit einem klaren Blick auf Qualität, Kosten, technische Komplexität und langfristige Stabilität. Mein Anspruch ist es, im Interesse meiner Kunden zu handeln — auch dann, wenn das bedeutet, Risiken, Schwachstellen oder unbequeme Wahrheiten offen anzusprechen.",
      values: [
        { title: "Kundeninteressen im Fokus", desc: "Ich denke technische Lösungen vom Ziel des Kunden aus — pragmatisch, nachvollziehbar und mit Blick auf Qualität, Kosten und Wartbarkeit." },
        { title: "Direkte Zusammenarbeit", desc: "Du arbeitest direkt mit mir als technischem Ansprechpartner zusammen. Das ermöglicht klare Kommunikation, kurze Abstimmungswege und ein gemeinsames Verständnis der Anforderungen." },
        { title: "Erfahrung in Enterprise-Projekten", desc: "Seit 2017 arbeite ich in komplexen IT-Umgebungen — unter anderem in Automotive-Projekten mit hohen Anforderungen an Qualität, Prozesse, Abstimmung und technische Stabilität." },
        { title: "Qualitätssicherung von Anfang an", desc: "Qualität entsteht nicht erst am Ende eines Projekts. Ich bringe Erfahrung in E2E-Testing, Testanalyse, Bug-Dokumentation, Reviews, CI/CD und stabilen Entwicklungsprozessen mit." },
        { title: "KI-Verständnis mit Praxisbezug", desc: "Durch meine Weiterbildung in Applied AI & Data Science bringe ich strukturiertes KI-Know-how mit — von Machine Learning und Deep Learning bis zu Generative AI, RAG und Prompt Engineering." },
      ],
    },
    services: {
      label: "Meine Leistungen",
      title: "Sechs Bereiche, in denen ich",
      titleAccent: "Wirkung",
      titleEnd: "entfalte.",
      seeAll: "Alle Details",
      jsTsCta: "Mehr über meine Erfahrungen mit JavaScript & TypeScript",
      javaSpringCta: "Mehr über meine Erfahrungen mit Java & Spring Boot Backend",
      qaTestCta: "Mehr über meine Erfahrungen mit QA & Test Management",
      aiConsultingCta: "Mehr über meinen KI-Background",
      hero: {
        label: "Leistungen",
        title: "Sechs Wege, wie ich dein Projekt",
        accent: "voranbringe",
        intro: "Von der Backend-Architektur bis zur KI-Integration – ich bringe technische Exzellenz mit strategischem Verständnis zusammen, damit dein Vorhaben messbaren Wert liefert.",
      },
      jumpToStack: "Zum Tech-Stack springen",
      marqueeHint: "Bewege die Maus über eine Karte, um zu pausieren",
      stackHeading: "Werkzeuge im",
      stackAccent: "Detail",
      stackLabel: "Tech-Stack",
      legend: "● Grundkenntnisse · ●● Fortgeschritten · ●●● Sehr gute Kenntnisse · ",
      legendYears: "= Jahre Erfahrung",
      matchTitle: "Klingt nach einer",
      matchAccent: "Passung",
      matchEnd: "?",
      matchDesc: "30 Minuten Erstgespräch – kostenfrei. Sag mir, was du baust, ich sage dir, ob ich helfen kann.",
    },
    process: {
      label: "Wie wir arbeiten",
      titleA: "Von",
      titleAccent: "erster Idee",
      titleB: "zu produktivem Code.",
      desc: "Mein Prozess ist transparent, schnell und auf dein Tempo angepasst. Keine unnötigen Meetings, keine versteckten Kosten – nur klare Schritte zu deinem Ziel.",
      steps: [
        { title: "Kennenlernen", desc: "30 Min Video-Call, kostenfrei und unverbindlich. Du erzählst von deinem Vorhaben, ich höre zu und gebe einen ehrlichen ersten Eindruck." },
        { title: "Scope & Angebot", desc: "Klare schriftliche Skizze: was wir bauen, in welchem Zeitraum, zu welchem Tagessatz. Keine versteckten Kosten." },
        { title: "Umsetzung", desc: "Agile Sprints (1–2 Wochen), wöchentliche Demos, Tool deiner Wahl (Jira, Linear, GitHub). Ich bin Teil deines Teams." },
        { title: "Übergabe & Support", desc: "Saubere Dokumentation, Wissenstransfer, optionaler Wartungsvertrag. Du bleibst nicht alleine zurück." },
      ],
    },
    teaser: {
      label: "Hi, ich bin Karim",
      titleA: "Entwicklung, Testen und Planen machen",
      titleAccent: "Spaß",
      titleEnd: "!",
      intro: "Geboren in Tunesien, ausgebildet in München, zuhause zwischen Deutschland und Portugal. Seit über 9 Jahren entwickle ich Software für Enterprise-Kunden und bin überzeugt: <strong>„Es geht nicht\" gibt es nicht.</strong>",
    },
    quote: {
      text: "Ich entwickle Software wie ein guter Handwerker arbeitet: <gold>strukturiert, präzise</gold>, mit Liebe zum Detail – und der festen Überzeugung, dass jedes Problem seine Lösung hat.",
      role: "IT-Consultant · München / Lissabon",
    },
    blog: {
      label: "Aus dem Journal",
      titleA: "Gedanken aus dem",
      titleAccent: "Maschinenraum",
      titleEnd: ".",
      heroLabel: "Insights & Journal",
      heroTitleA: "Notizen aus dem",
      heroTitleAccent: "Engineering-Alltag",
      heroIntro: "Gedanken, Lektionen und Tutorials aus echten Projekten – verständlich, pragmatisch und ohne Buzzword-Bingo.",
      filterAll: "Alle",
      noPosts: "Keine Beiträge gefunden.",
      ctaTitleA: "Hat dich ein Thema",
      ctaTitleAccent: "getriggert",
      ctaTitleEnd: "?",
      ctaDesc: "Sprich mich gerne darauf an – ich freue mich über jeden Austausch.",
      ctaButton: "Gespräch buchen",
      postNotFound: "Beitrag nicht gefunden",
      readingTime: "min",
      inspired: "Hat dich der Beitrag inspiriert?",
      letsTalk: "Lass uns sprechen.",
    },
    cta: {
      label: "Bereit für den nächsten Schritt",
      titleA: "Lass uns dein",
      titleAccent: "Projekt",
      titleEnd: "auf die Spur bringen.",
      desc: "30 Minuten, kostenfrei, unverbindlich. Ich höre zu, stelle Fragen und gebe dir eine ehrliche Einschätzung – auch wenn ich nicht der richtige Partner bin.",
      response: "Antwort innerhalb 48 Stunden",
    },
    about: {
      label: "Über mich",
      titleA: "Hi, ich bin",
      titleAccent: "Karim",
      titleEnd: ".",
      titleSub: "Engineer mit Herz und Plan.",
      buttonTalk: "Lass uns sprechen",
      buttonCv: "Lebenslauf ansehen",
      experience: "Erfahrung",
      certificates: "Zertifikate",
      birthday: "Geburtstag",
      bornIn: "Geboren in",
      location: "Standort",
      languages: "Sprachen",
      langSectionLabel: "Sprachen",
      langTitleA: "Vier Sprachen,",
      langTitleAccent: "eine Mission",
      langTitleEnd: ".",
      langDesc: "Globale Kommunikation gehört zu meinem Alltag – ob im Stand-Up, im Stakeholder-Workshop oder beim Smalltalk an der Kaffeemaschine.",
      native: "Native",
      principlesLabel: "Meine Prinzipien",
      principlesTitleA: "Worte, an die ich mich",
      principlesAccent: "halte",
      principlesEnd: ".",
      certsLabel: "Zertifizierungen",
      certsTitleA: "Geprüft &",
      certsAccent: "zertifiziert",
      certsEnd: ".",
      downloadCert: "Zertifikat herunterladen",
      allStations: "Alle Stationen",
      coreLabel: "Kernfähigkeiten",
      coreTitleA: "Wo mein",
      coreAccent: "Fokus",
      coreEnd: "liegt.",
      availability: "Verfügbarkeit",
      workMode: "Arbeitsmodus",
      nationality: "Nationalität",
      behindLabel: "Hinter dem Code",
      behindA: "Was mich auch",
      behindAccent: "bewegt",
      behindEnd: ".",
      matchTitle: "Klingt nach einem",
      matchAccent: "Match",
      matchEnd: "?",
      finalCta: "Erstgespräch buchen",
    },
    exp: {
      label: "Werdegang & Lebenslauf",
      titleA: "Stationen, die mich",
      titleAccent: "geformt",
      titleEnd: "haben.",
      intro: "Über zwölf Stationen seit 2012 – von Online-Marketing über Frontend & QA bis zu Backend-Microservices und Product Ownership bei Audi, BMW und CARIAD.",
      cvSince: "Stand 08/2026",
      stationsLabel: "Stationen",
      yearsExpLabel: "Erfahrung",
      yearsExpValue: "9+ Jahre",
      certsLabel: "Zertifikate",
      studyLabel: "Studium",
      studyValue: "LMU München",
      workLabel: "Berufserfahrung",
      workTitleA: "Vom",
      workAccent: "Werkstudenten",
      workEnd: "zum Senior-Consultant.",
      client: "Kunde:",
      techStack: "Tech-Stack",
      certsHeading: "Zertifizierungen",
      certsHeadingA: "Geprüft &",
      certsHeadingAccent: "zertifiziert",
      certsHeadingEnd: ".",
      downloadCert: "Zertifikat herunterladen",
      eduLabel: "Ausbildung",
      eduTitleA: "Mein",
      eduAccent: "akademischer Weg",
      eduEnd: ".",
      ctaTitleA: "Sieht das nach",
      ctaTitleAccent: "deinem Profil",
      ctaTitleEnd: "aus?",
      ctaDesc: "Lass uns sprechen – ich freue mich auf deinen Use-Case.",
      ctaButton: "Gespräch buchen",
    },
    types: {
      freelance: "Freelance",
      permanent: "Festanstellung",
      student: "Werkstudent",
      leave: "Auszeit",
      temp: "Zeitarbeit",
      academic: "Studium",
    },
    contact: {
      heroLabel: "Kontakt",
      heroTitleA: "Schreib mir –",
      heroAccent: "ich höre zu",
      heroEnd: ".",
      email: "E-Mail",
      phone: "Telefon",
      location: "Standort",
      responseTitle: "Antwortzeit",
      responseValue: "Üblicherweise < 48 Stunden",
      fields: {
        name: "Name",
        email: "E-Mail",
        subject: "Betreff",
        phone: "Telefon (optional)",
        message: "Nachricht",
      },
      submit: "Nachricht senden",
      sending: "Sende...",
      success: "Danke! Ich melde mich zeitnah bei dir.",
    },
    appt: {
      heroLabel: "Termin buchen",
      heroTitleA: "Ein",
      heroAccent: "30-minütiges",
      heroEnd: "Gespräch.",
      heroIntro: "Kostenfrei, unverbindlich und ohne Verkaufsdruck. Wir lernen uns kennen, du erzählst von deinem Projekt – und ich gebe dir einen ehrlichen ersten Eindruck, ob ich der richtige Sparringspartner bin.",
      howTitle: "Wie es abläuft",
      howSteps: [
        "Du wählst Wunschdatum und -uhrzeit.",
        "Ich bestätige innerhalb von 24 Stunden per E-Mail.",
        "Wir treffen uns per Video-Call (Link folgt).",
        "Im Anschluss bekommst du eine Kurz-Notiz.",
      ],
      goodToKnow: "Gut zu wissen",
      goodPoints: [
        "Sprache: Deutsch, Englisch oder Französisch",
        "Tool: Google Meet oder MS Teams",
        "Zeitzone: MEZ / Europe/Berlin",
        "NDA auf Wunsch vorab",
      ],
      fields: {
        name: "Name",
        email: "E-Mail",
        company: "Unternehmen (optional)",
        phone: "Telefon (optional)",
        topic: "Worum geht's?",
        date: "Wunschdatum",
        time: "Uhrzeit",
        message: "Hintergrund / Kurze Notiz (optional)",
      },
      topicDefault: "Kennenlern-Gespräch (30 Min.)",
      submit: "Termin anfragen",
      sending: "Sende...",
      success: "Termin angefragt! Ich bestätige deinen Wunschtermin per E-Mail – meist innerhalb von 24 Stunden.",
    },
    footer: {
      tagline: "Freelance IT-Consultant & Softwareentwickler aus Deutschland und Portugal. Über 9 Jahre Erfahrung in Backend-Engineering, Quality Assurance und Projektkoordination für Enterprise-Kunden.",
      navHeading: "Navigation",
      contactHeading: "Direkter Draht",
      cvLink: "Lebenslauf (PDF) ↓",
      copyright: "© {year} Karim Benna · IT Consulting — Limitless Solutions.",
      imprint: "Impressum",
      privacy: "Datenschutz",
    },
    footerCallout: {
      ctaTitleA: "Lass uns deine nächste",
      ctaAccent: "Idee",
      ctaTitleEnd: "zum Laufen bringen.",
      ctaDesc: "Wenn du nach einem zuverlässigen IT-Berater suchst, der mitdenkt, Verantwortung übernimmt und Ergebnisse liefert – bist du hier richtig.",
    },
    notFound: {
      title: "Seite nicht gefunden",
      back: "Zurück zur Startseite",
    },
  },

  en: {
    seo: {
      home: {
        title: "Karim Benna – Freelance IT Consultant for Backend, QA & AI",
        description: "Freelance IT consultant with 9+ years of experience in Java/Spring Boot, QA, product ownership and applied AI. Worked with Audi, BMW and CARIAD.",
      },
      about: {
        title: "About Karim Benna",
        description: "Senior IT consultant, MIT-certified in Applied AI & Data Science, fluent in 4 languages (DE, EN, FR, AR) – based in Germany and Portugal.",
      },
      services: {
        title: "Services: Backend, QA, AI consulting & Product Ownership",
        description: "Six core areas: Java/Spring Boot backend, JavaScript/TypeScript/GraphQL, React Native, ISTQB-certified QA, product ownership and AI consulting.",
      },
      experience: {
        title: "Career & CV",
        description: "12 positions since 2012 – from online marketing to frontend & QA, to backend microservices and product ownership at Audi, BMW and CARIAD.",
      },
      blog: {
        title: "Blog – IT consulting, backend, QA & AI",
        description: "Hands-on articles on Java/Spring Boot, JavaScript/TypeScript, ISTQB-style QA, product ownership and applied AI from a senior IT consultant.",
      },
      contact: {
        title: "Contact",
        description: "Reach out – freelance IT consulting requests, project discovery or sparring. Typically responding within 48 hours.",
      },
    },
    projects: {
      label: "Selected AI / Data projects",
      title: "Real",
      titleAccent: "AI projects",
      titleEnd: " I've shipped.",
      intro: "Three capstone and course projects from the MIT Applied AI & Data Science program – ranging from a generative-AI feedback system to deep-learning image recognition and exploratory data analysis.",
      programLabel: "Program",
      programName: "MIT Applied AI & Data Science",
      viewLink: "View project",
      fullPortfolio: "See the full portfolio on Great Learning",
      items: {
        "retail-genai": {
          course: "Capstone · Generative AI",
          title: "Real-Time Retail Feedback Intelligence",
          desc: "Generative-AI system for \"ChicStyle\" that analyses thousands of customer reviews in real time. Uses zero-shot, few-shot and chain-of-thought prompting to detect sentiment, the product or service involved and urgent issues – and to draft personalised replies that help the team respond faster.",
        },
        "svhn-cnn": {
          course: "Elective · Deep Learning",
          title: "Street-View Housing Number – Digit Recognition",
          desc: "Deep-learning solution to recognise digits from real-world street imagery (SVHN dataset, .h5). Multiple ANN and CNN architectures were trained, compared and evaluated to pick the best-performing model for accurate digit prediction.",
        },
        "foodhub-eda": {
          course: "Foundations of AI",
          title: "FoodHub – Order Analysis",
          desc: "Exploratory data analysis for a food aggregator: customer order behaviour, restaurant performance and demand trends were uncovered with NumPy/Pandas and visualisations, yielding data-driven recommendations for a better customer experience.",
        },
      },
    },
    common: {
      bookCall: "Get in touch",
      bookFree: "Get in touch",
      seeServices: "See services",
      sendMessage: "Send message",
      contactMe: "Send message",
      moreAbout: "More about me",
      readMore: "Read more",
      readArticle: "Read",
      allArticles: "All posts",
      cvPdf: "Download CV (PDF)",
      bookAppointment: "Get in touch",
      preferEmail: "Send an email",
      responseTime: "Response time",
      languageLabel: "Switch language",
      learnMore: "Learn more",
      requestProject: "Request a project",
      backToBlog: "All posts",
      backHome: "Back to home",
      seeMatch: "Match",
      sendButton: "Send",
      loading: "Loading…",
      saving: "Sending...",
    },
    nav: {
      home: "Home",
      about: "Who I am",
      services: "Services",
      experience: "Career",
      blog: "Insights",
      contact: "Contact",
    },
    hero: {
      pillAvailable: "Currently accepting projects for Q3/2026",
      titleA: "Your Senior",
      titleAccent: "IT Consultant",
      titleB: "for complex Backend &",
      titleC: "Quality projects.",
      intro:
        "I'm <strong>Karim Benna</strong>, a freelance IT consultant for backend development, quality assurance and technical project coordination. Since 2017 I've been helping <gold>enterprise projects</gold> deliver complex software solutions that are stable, transparent and future-proof.",
      tagline: "Software development, quality assurance and technical coordination from a single source.",
      stats: {
        yearsExp: "Years experience",
        stations: "Positions",
        certs: "Certifications (ISTQB · MIT)",
      },
      availability: "Availability",
      mode: "Mode",
      location: "Location",
      clientStrip: "Trusted by",
      responseShort: "Typically < 48 hrs",
      langChips: "Deutsch · English · Français",
    },
    quick: {
      label: "Quick access",
      title: "What's",
      titleAccent: "in here",
      titleEnd: ".",
      tools: { label: "Tech stack", title: "Tools in detail" },
      certs: { label: "Certificates", title: "Download certifications" },
      experience: { label: "Career", title: "List of positions" },
      services: { label: "Services", title: "Areas of competence" },
    },
    why: {
      label: "Why work with me",
      title: "What you can expect from",
      titleAccent: "working with me",
      desc: "I help you move your project forward with a clear focus on quality, cost, technical complexity and long-term stability. My commitment is to act in my clients' best interest — even when that means openly addressing risks, weak spots or uncomfortable truths.",
      values: [
        { title: "Client interests at the centre", desc: "I approach technical solutions from the client's goal — pragmatically, transparently, and with an eye on quality, cost and maintainability." },
        { title: "Direct collaboration", desc: "You work directly with me as your technical counterpart. That means clear communication, short feedback loops and a shared understanding of what needs to be built." },
        { title: "Experience in enterprise projects", desc: "Since 2017 I've been working in complex IT environments — including automotive projects with high demands on quality, processes, alignment and technical stability." },
        { title: "Quality from day one", desc: "Quality is not added at the end of a project. I bring experience in E2E testing, test analysis, bug documentation, reviews, CI/CD and stable development processes." },
        { title: "Practical AI know-how", desc: "Thanks to my Applied AI & Data Science training, I bring structured AI expertise — from machine learning and deep learning to generative AI, RAG and prompt engineering." },
      ],
    },
    services: {
      label: "My services",
      title: "Six areas where I create",
      titleAccent: "impact",
      titleEnd: ".",
      seeAll: "See all details",
      jsTsCta: "More about my experience with JavaScript & TypeScript",
      javaSpringCta: "More about my experience with Java & Spring Boot Backend",
      qaTestCta: "More about my experience with QA & Test Management",
      aiConsultingCta: "More about my AI background",
      hero: {
        label: "Services",
        title: "Six ways I move your project",
        accent: "forward",
        intro: "From backend architecture to AI integration – I bring technical excellence and strategic thinking together so your venture delivers measurable value.",
      },
      jumpToStack: "Jump to tech stack",
      marqueeHint: "Hover a card to pause",
      stackHeading: "Tools in",
      stackAccent: "detail",
      stackLabel: "Tech stack",
      legend: "● Basics · ●● Advanced · ●●● Expert · ",
      legendYears: "= years of experience",
      matchTitle: "Sounds like a",
      matchAccent: "fit",
      matchEnd: "?",
      matchDesc: "30-minute intro call – free of charge. Tell me what you're building, I'll tell you if I can help.",
    },
    process: {
      label: "How we work",
      titleA: "From",
      titleAccent: "first idea",
      titleB: "to production code.",
      desc: "My process is transparent, fast and adapted to your pace. No unnecessary meetings, no hidden costs – only clear steps towards your goal.",
      steps: [
        { title: "Intro call", desc: "30 min video call, free and non-binding. You share your project, I listen and give an honest first impression." },
        { title: "Scope & quote", desc: "Clear written outline: what we build, in what timeframe, at what daily rate. No hidden fees." },
        { title: "Delivery", desc: "Agile sprints (1–2 weeks), weekly demos, your tool of choice (Jira, Linear, GitHub). I'm part of your team." },
        { title: "Handover & support", desc: "Clean documentation, knowledge transfer, optional maintenance contract. You're not left alone." },
      ],
    },
    teaser: {
      label: "Hi, I'm Karim",
      titleA: "Building, testing and planning is",
      titleAccent: "fun",
      titleEnd: "!",
      intro: "Born in Tunisia, educated in Munich, at home between Germany and Portugal. For 9+ years I've been building software for enterprise clients and I'm convinced: <strong>\"It can't be done\" doesn't exist.</strong>",
    },
    quote: {
      text: "I build software the way a good craftsman works: <gold>structured, precise</gold>, with attention to detail – and the firm conviction that every problem has a solution.",
      role: "IT Consultant · Munich / Lisbon",
    },
    blog: {
      label: "From the journal",
      titleA: "Notes from the",
      titleAccent: "engine room",
      titleEnd: ".",
      heroLabel: "Insights & journal",
      heroTitleA: "Notes from daily",
      heroTitleAccent: "engineering work",
      heroIntro: "Thoughts, lessons and tutorials from real projects – clear, pragmatic, no buzzword bingo.",
      filterAll: "All",
      noPosts: "No posts found.",
      ctaTitleA: "Did a topic",
      ctaTitleAccent: "spark your interest",
      ctaTitleEnd: "?",
      ctaDesc: "Drop me a line – I enjoy every conversation.",
      ctaButton: "Book a call",
      postNotFound: "Post not found",
      readingTime: "min",
      inspired: "Did this post inspire you?",
      letsTalk: "Let's talk.",
    },
    cta: {
      label: "Ready for the next step",
      titleA: "Let's get your",
      titleAccent: "project",
      titleEnd: "on track.",
      desc: "30 minutes, free of charge, no strings attached. I listen, ask questions and give you an honest assessment – even if I'm not the right partner.",
      response: "Reply within 48 hours",
    },
    about: {
      label: "About me",
      titleA: "Hi, I'm",
      titleAccent: "Karim",
      titleEnd: ".",
      titleSub: "Engineer with heart and plan.",
      buttonTalk: "Let's talk",
      buttonCv: "View CV",
      experience: "Experience",
      certificates: "Certificates",
      birthday: "Birthday",
      bornIn: "Born in",
      location: "Location",
      languages: "Languages",
      langSectionLabel: "Languages",
      langTitleA: "Four languages,",
      langTitleAccent: "one mission",
      langTitleEnd: ".",
      langDesc: "Global communication is part of my daily life – be it in a stand-up, a stakeholder workshop or coffee-machine small talk.",
      native: "Native",
      principlesLabel: "My principles",
      principlesTitleA: "Words I",
      principlesAccent: "live by",
      principlesEnd: ".",
      certsLabel: "Certifications",
      certsTitleA: "Verified &",
      certsAccent: "certified",
      certsEnd: ".",
      downloadCert: "Download certificate",
      allStations: "All positions",
      coreLabel: "Core strengths",
      coreTitleA: "Where my",
      coreAccent: "focus",
      coreEnd: "lies.",
      availability: "Availability",
      workMode: "Work mode",
      nationality: "Nationality",
      behindLabel: "Behind the code",
      behindA: "What also",
      behindAccent: "moves me",
      behindEnd: ".",
      matchTitle: "Sounds like a",
      matchAccent: "match",
      matchEnd: "?",
      finalCta: "Book intro call",
    },
    exp: {
      label: "Career & CV",
      titleA: "Stations that",
      titleAccent: "shaped",
      titleEnd: "me.",
      intro: "Twelve+ positions since 2012 – from online marketing to frontend & QA, to backend microservices and product ownership at Audi, BMW and CARIAD.",
      cvSince: "Updated 08/2026",
      stationsLabel: "Positions",
      yearsExpLabel: "Experience",
      yearsExpValue: "9+ years",
      certsLabel: "Certificates",
      studyLabel: "Studies",
      studyValue: "LMU Munich",
      workLabel: "Work experience",
      workTitleA: "From",
      workAccent: "working student",
      workEnd: "to senior consultant.",
      client: "Client:",
      techStack: "Tech stack",
      certsHeading: "Certifications",
      certsHeadingA: "Verified &",
      certsHeadingAccent: "certified",
      certsHeadingEnd: ".",
      downloadCert: "Download certificate",
      eduLabel: "Education",
      eduTitleA: "My",
      eduAccent: "academic path",
      eduEnd: ".",
      ctaTitleA: "Sounds like",
      ctaTitleAccent: "your profile",
      ctaTitleEnd: "?",
      ctaDesc: "Let's talk – I look forward to your use case.",
      ctaButton: "Book a call",
    },
    types: {
      freelance: "Freelance",
      permanent: "Permanent",
      student: "Working student",
      leave: "On leave",
      temp: "Temporary",
      academic: "Academic",
    },
    contact: {
      heroLabel: "Contact",
      heroTitleA: "Write to me –",
      heroAccent: "I'm listening",
      heroEnd: ".",
      email: "E-mail",
      phone: "Phone",
      location: "Location",
      responseTitle: "Response time",
      responseValue: "Typically < 48 hours",
      fields: {
        name: "Name",
        email: "E-mail",
        subject: "Subject",
        phone: "Phone (optional)",
        message: "Message",
      },
      submit: "Send message",
      sending: "Sending...",
      success: "Thank you! I'll get back to you shortly.",
    },
    appt: {
      heroLabel: "Book a call",
      heroTitleA: "A",
      heroAccent: "30-minute",
      heroEnd: "call.",
      heroIntro: "Free, no obligation, no sales pressure. We get to know each other, you tell me about your project – and I give you an honest first impression on whether I'm the right sparring partner.",
      howTitle: "How it works",
      howSteps: [
        "Pick your preferred date and time.",
        "I confirm within 24 hours by e-mail.",
        "We meet on a video call (link to follow).",
        "Afterwards you receive a short note.",
      ],
      goodToKnow: "Good to know",
      goodPoints: [
        "Language: German, English or French",
        "Tool: Google Meet or MS Teams",
        "Timezone: CET / Europe/Berlin",
        "NDA on request in advance",
      ],
      fields: {
        name: "Name",
        email: "E-mail",
        company: "Company (optional)",
        phone: "Phone (optional)",
        topic: "What's it about?",
        date: "Preferred date",
        time: "Time",
        message: "Background / short note (optional)",
      },
      topicDefault: "Intro call (30 min)",
      submit: "Request appointment",
      sending: "Sending...",
      success: "Request received! I'll confirm your preferred slot by e-mail – usually within 24 hours.",
    },
    footer: {
      tagline: "Freelance IT consultant & software engineer based in Germany and Portugal. 9+ years of experience in backend engineering, quality assurance and project coordination for enterprise clients.",
      navHeading: "Navigation",
      contactHeading: "Direct line",
      cvLink: "CV (PDF) ↓",
      copyright: "© {year} Karim Benna · IT Consulting — Limitless Solutions.",
      imprint: "Imprint",
      privacy: "Privacy",
    },
    footerCallout: {
      ctaTitleA: "Let's get your next",
      ctaAccent: "idea",
      ctaTitleEnd: "running.",
      ctaDesc: "If you're looking for a reliable IT consultant who thinks along, takes ownership and delivers results – you're in the right place.",
    },
    notFound: {
      title: "Page not found",
      back: "Back to home",
    },
  },

  fr: {
    seo: {
      home: {
        title: "Karim Benna – Consultant IT freelance : backend, QA & IA",
        description: "Consultant IT freelance avec plus de 9 ans d'expérience en Java/Spring Boot, QA, product ownership et IA appliquée. Missions chez Audi, BMW et CARIAD.",
      },
      about: {
        title: "À propos de Karim Benna",
        description: "Consultant IT senior, certifié MIT en Applied AI & Data Science, polyglotte (DE, EN, FR, AR) – basé entre l'Allemagne et le Portugal.",
      },
      services: {
        title: "Services : Backend, QA, conseil IA & Product Ownership",
        description: "Six domaines clés : backend Java/Spring Boot, JavaScript/TypeScript/GraphQL, React Native, QA certifiée ISTQB, product ownership et conseil IA.",
      },
      experience: {
        title: "Parcours & CV",
        description: "12 postes depuis 2012 – du marketing en ligne au frontend & QA, jusqu'aux microservices backend et au product ownership chez Audi, BMW et CARIAD.",
      },
      blog: {
        title: "Blog – conseil IT, backend, QA & IA",
        description: "Articles concrets sur Java/Spring Boot, JavaScript/TypeScript, QA façon ISTQB, product ownership et IA appliquée, par un consultant IT senior.",
      },
      contact: {
        title: "Contact",
        description: "Contactez-moi pour vos demandes de conseil IT freelance, cadrage de projet ou sparring. Réponse habituellement sous 48 heures.",
      },
    },
    projects: {
      label: "Projets IA / Data sélectionnés",
      title: "De vrais",
      titleAccent: "projets IA",
      titleEnd: " que j'ai livrés.",
      intro: "Trois projets de capstone et de cours issus du programme MIT Applied AI & Data Science : d'un système d'IA générative à la reconnaissance d'images par deep learning, en passant par l'analyse exploratoire de données.",
      programLabel: "Programme",
      programName: "MIT Applied AI & Data Science",
      viewLink: "Voir le projet",
      fullPortfolio: "Voir le portfolio complet sur Great Learning",
      items: {
        "retail-genai": {
          course: "Capstone · Generative AI",
          title: "Real-Time Retail Feedback Intelligence",
          desc: "Système d'IA générative pour « ChicStyle » qui analyse en temps réel des milliers d'avis clients. Il utilise le zero-shot, le few-shot et le chain-of-thought prompting pour détecter le sentiment, le produit ou service concerné et les problèmes urgents – et rédige des réponses personnalisées qui aident l'équipe à répondre plus vite.",
        },
        "svhn-cnn": {
          course: "Elective · Deep Learning",
          title: "Street-View Housing Number – Digit Recognition",
          desc: "Solution de deep learning pour reconnaître des chiffres dans des photos de rue réelles (jeu de données SVHN, .h5). Plusieurs architectures ANN et CNN ont été entraînées, comparées et évaluées pour sélectionner le modèle le plus performant.",
        },
        "foodhub-eda": {
          course: "Foundations of AI",
          title: "FoodHub – Order Analysis",
          desc: "Analyse exploratoire de données pour un agrégateur de restauration : comportements de commande, performance des restaurants et tendances de demande ont été révélés à l'aide de NumPy/Pandas et de visualisations, débouchant sur des recommandations data-driven.",
        },
      },
    },
    common: {
      bookCall: "Me contacter",
      bookFree: "Me contacter",
      seeServices: "Voir les prestations",
      sendMessage: "Envoyer un message",
      contactMe: "Envoyer un message",
      moreAbout: "En savoir plus sur moi",
      readMore: "Lire la suite",
      readArticle: "Lire",
      allArticles: "Tous les articles",
      cvPdf: "Télécharger le CV (PDF)",
      bookAppointment: "Me contacter",
      preferEmail: "Envoyer un e-mail",
      responseTime: "Temps de réponse",
      languageLabel: "Changer de langue",
      learnMore: "En savoir plus",
      requestProject: "Demander un projet",
      backToBlog: "Tous les articles",
      backHome: "Retour à l'accueil",
      seeMatch: "Match",
      sendButton: "Envoyer",
      loading: "Chargement…",
      saving: "Envoi...",
    },
    nav: {
      home: "Accueil",
      about: "Qui je suis",
      services: "Prestations",
      experience: "Parcours",
      blog: "Insights",
      contact: "Contact",
    },
    hero: {
      pillAvailable: "Demandes pour Q3/2026 actuellement ouvertes",
      titleA: "Votre",
      titleAccent: "consultant IT senior",
      titleB: "pour vos projets backend &",
      titleC: "qualité complexes.",
      intro:
        "Je suis <strong>Karim Benna</strong>, consultant IT indépendant en développement backend, assurance qualité et coordination technique de projets. Depuis 2017, j'accompagne des <gold>projets enterprise</gold> dans la mise en œuvre de solutions logicielles complexes — stables, lisibles et pérennes.",
      tagline: "Développement logiciel, assurance qualité et coordination technique réunis chez un seul interlocuteur.",
      stats: {
        yearsExp: "Ans d'expérience",
        stations: "Postes",
        certs: "Certifications (ISTQB · MIT)",
      },
      availability: "Disponibilité",
      mode: "Mode",
      location: "Lieu",
      clientStrip: "Ils m'ont fait confiance",
      responseShort: "Habituellement < 48 h",
      langChips: "Deutsch · English · Français",
    },
    quick: {
      label: "Accès rapide",
      title: "Ce qui",
      titleAccent: "vous attend",
      titleEnd: ".",
      tools: { label: "Stack technique", title: "Outils en détail" },
      certs: { label: "Certificats", title: "Télécharger les certificats" },
      experience: { label: "Parcours", title: "Liste des postes" },
      services: { label: "Services", title: "Domaines de compétence" },
    },
    why: {
      label: "Pourquoi moi",
      title: "Ce que vous pouvez attendre",
      titleAccent: "de notre collaboration",
      desc: "Je vous accompagne dans l'évolution de votre projet avec un regard clair sur la qualité, les coûts, la complexité technique et la stabilité à long terme. Mon engagement : agir dans l'intérêt de mes clients — y compris lorsqu'il s'agit d'évoquer ouvertement les risques, les faiblesses ou les vérités inconfortables.",
      values: [
        { title: "L'intérêt du client au centre", desc: "J'aborde les solutions techniques en partant de l'objectif du client — pragmatique, transparent et attentif à la qualité, aux coûts et à la maintenabilité." },
        { title: "Collaboration directe", desc: "Vous travaillez directement avec moi comme interlocuteur technique. Cela permet une communication claire, des boucles de décision courtes et une compréhension commune des besoins." },
        { title: "Expérience sur projets enterprise", desc: "Depuis 2017, j'évolue dans des environnements IT complexes — notamment des projets automobiles exigeants en matière de qualité, de processus, d'alignement et de stabilité technique." },
        { title: "Assurance qualité dès le départ", desc: "La qualité ne s'ajoute pas à la fin d'un projet. J'apporte de l'expérience en tests E2E, analyse de tests, documentation de bugs, revues, CI/CD et processus de développement robustes." },
        { title: "IA appliquée & opérationnelle", desc: "Grâce à ma formation Applied AI & Data Science, j'apporte un savoir-faire IA structuré — du machine learning et du deep learning à l'IA générative, au RAG et au prompt engineering." },
      ],
    },
    services: {
      label: "Mes prestations",
      title: "Six domaines où je crée de",
      titleAccent: "l'impact",
      titleEnd: ".",
      seeAll: "Voir tous les détails",
      jsTsCta: "En savoir plus sur mon expérience avec JavaScript & TypeScript",
      javaSpringCta: "En savoir plus sur mon expérience avec Java & Spring Boot Backend",
      qaTestCta: "En savoir plus sur mon expérience avec la QA & le Test Management",
      aiConsultingCta: "En savoir plus sur mon parcours en IA",
      hero: {
        label: "Prestations",
        title: "Six façons de faire avancer votre",
        accent: "projet",
        intro: "De l'architecture backend à l'intégration IA – j'allie excellence technique et compréhension stratégique pour que votre projet génère une valeur mesurable.",
      },
      jumpToStack: "Aller au stack technique",
      marqueeHint: "Survolez une carte pour mettre en pause",
      stackHeading: "Outils en",
      stackAccent: "détail",
      stackLabel: "Stack technique",
      legend: "● Bases · ●● Avancé · ●●● Expert · ",
      legendYears: "= années d'expérience",
      matchTitle: "Ça ressemble à un",
      matchAccent: "match",
      matchEnd: " ?",
      matchDesc: "30 minutes d'entretien – gratuit. Dites-moi ce que vous construisez, je vous dis si je peux aider.",
    },
    process: {
      label: "Comment on travaille",
      titleA: "De la",
      titleAccent: "première idée",
      titleB: "au code en production.",
      desc: "Mon processus est transparent, rapide et adapté à votre rythme. Pas de réunions inutiles, pas de coûts cachés – uniquement des étapes claires vers votre objectif.",
      steps: [
        { title: "Premier échange", desc: "Visio de 30 min, gratuit et sans engagement. Vous présentez votre projet, j'écoute et donne une première impression honnête." },
        { title: "Cadrage & devis", desc: "Synthèse écrite claire : ce qu'on construit, dans quel délai, à quel TJM. Pas de coûts cachés." },
        { title: "Réalisation", desc: "Sprints agiles (1–2 semaines), démos hebdomadaires, votre outil de choix (Jira, Linear, GitHub). Je fais partie de votre équipe." },
        { title: "Livraison & support", desc: "Documentation propre, transfert de connaissances, contrat de maintenance optionnel. Vous n'êtes pas laissé seul." },
      ],
    },
    teaser: {
      label: "Bonjour, je suis Karim",
      titleA: "Développer, tester et planifier, c'est",
      titleAccent: "amusant",
      titleEnd: " !",
      intro: "Né en Tunisie, formé à Munich, partagé entre l'Allemagne et le Portugal. Depuis 9+ ans, je développe des logiciels pour des clients enterprise et je suis convaincu : <strong>« C'est impossible » n'existe pas.</strong>",
    },
    quote: {
      text: "Je code comme un bon artisan travaille : <gold>structuré, précis</gold>, avec souci du détail – et la conviction que chaque problème a sa solution.",
      role: "Consultant IT · Munich / Lisbonne",
    },
    blog: {
      label: "Du journal",
      titleA: "Notes depuis la",
      titleAccent: "salle des machines",
      titleEnd: ".",
      heroLabel: "Insights & journal",
      heroTitleA: "Notes du",
      heroTitleAccent: "quotidien d'ingénieur",
      heroIntro: "Idées, leçons et tutoriels tirés de projets réels – clairs, pragmatiques, sans buzzwords.",
      filterAll: "Tous",
      noPosts: "Aucun article trouvé.",
      ctaTitleA: "Un sujet vous a",
      ctaTitleAccent: "interpellé",
      ctaTitleEnd: " ?",
      ctaDesc: "Échangeons – chaque discussion m'intéresse.",
      ctaButton: "Prendre rendez-vous",
      postNotFound: "Article introuvable",
      readingTime: "min",
      inspired: "Cet article vous a inspiré ?",
      letsTalk: "Parlons-en.",
    },
    cta: {
      label: "Prêt pour la prochaine étape",
      titleA: "Mettons votre",
      titleAccent: "projet",
      titleEnd: "sur les rails.",
      desc: "30 minutes, gratuit, sans engagement. J'écoute, je pose des questions et je vous donne un avis honnête – même si je ne suis pas le bon partenaire.",
      response: "Réponse sous 48 heures",
    },
    about: {
      label: "À propos",
      titleA: "Bonjour, je suis",
      titleAccent: "Karim",
      titleEnd: ".",
      titleSub: "Ingénieur avec cœur et méthode.",
      buttonTalk: "Échangeons",
      buttonCv: "Voir le CV",
      experience: "Expérience",
      certificates: "Certificats",
      birthday: "Anniversaire",
      bornIn: "Né à",
      location: "Lieu",
      languages: "Langues",
      langSectionLabel: "Langues",
      langTitleA: "Quatre langues,",
      langTitleAccent: "une mission",
      langTitleEnd: ".",
      langDesc: "La communication internationale fait partie de mon quotidien – que ce soit en stand-up, en atelier ou autour de la machine à café.",
      native: "Native",
      principlesLabel: "Mes principes",
      principlesTitleA: "Des mots que",
      principlesAccent: "j'applique",
      principlesEnd: ".",
      certsLabel: "Certifications",
      certsTitleA: "Validé &",
      certsAccent: "certifié",
      certsEnd: ".",
      downloadCert: "Télécharger le certificat",
      allStations: "Tous les postes",
      coreLabel: "Compétences clés",
      coreTitleA: "Où se trouve mon",
      coreAccent: "focus",
      coreEnd: ".",
      availability: "Disponibilité",
      workMode: "Mode de travail",
      nationality: "Nationalité",
      behindLabel: "Derrière le code",
      behindA: "Ce qui me",
      behindAccent: "passionne aussi",
      behindEnd: ".",
      matchTitle: "Ça ressemble à un",
      matchAccent: "match",
      matchEnd: " ?",
      finalCta: "Réserver l'entretien",
    },
    exp: {
      label: "Parcours & CV",
      titleA: "Étapes qui m'ont",
      titleAccent: "façonné",
      titleEnd: ".",
      intro: "Plus de douze postes depuis 2012 – du marketing en ligne au frontend & QA, jusqu'aux microservices backend et au product ownership chez Audi, BMW et CARIAD.",
      cvSince: "MAJ 08/2026",
      stationsLabel: "Postes",
      yearsExpLabel: "Expérience",
      yearsExpValue: "9+ ans",
      certsLabel: "Certificats",
      studyLabel: "Études",
      studyValue: "LMU Munich",
      workLabel: "Expérience professionnelle",
      workTitleA: "De",
      workAccent: "étudiant",
      workEnd: "à consultant senior.",
      client: "Client :",
      techStack: "Stack technique",
      certsHeading: "Certifications",
      certsHeadingA: "Validé &",
      certsHeadingAccent: "certifié",
      certsHeadingEnd: ".",
      downloadCert: "Télécharger le certificat",
      eduLabel: "Formation",
      eduTitleA: "Mon",
      eduAccent: "parcours académique",
      eduEnd: ".",
      ctaTitleA: "Ça correspond à",
      ctaTitleAccent: "votre profil",
      ctaTitleEnd: " ?",
      ctaDesc: "Échangeons – votre cas d'usage m'intéresse.",
      ctaButton: "Prendre rendez-vous",
    },
    types: {
      freelance: "Freelance",
      permanent: "CDI",
      student: "Étudiant",
      leave: "Pause",
      temp: "Intérim",
      academic: "Études",
    },
    contact: {
      heroLabel: "Contact",
      heroTitleA: "Écrivez-moi –",
      heroAccent: "j'écoute",
      heroEnd: ".",
      email: "E-mail",
      phone: "Téléphone",
      location: "Lieu",
      responseTitle: "Temps de réponse",
      responseValue: "Habituellement < 48 h",
      fields: {
        name: "Nom",
        email: "E-mail",
        subject: "Sujet",
        phone: "Téléphone (optionnel)",
        message: "Message",
      },
      submit: "Envoyer le message",
      sending: "Envoi...",
      success: "Merci ! Je vous réponds rapidement.",
    },
    appt: {
      heroLabel: "Prendre rendez-vous",
      heroTitleA: "Un entretien de",
      heroAccent: "30 minutes",
      heroEnd: ".",
      heroIntro: "Gratuit, sans engagement, sans pression commerciale. Nous faisons connaissance, vous présentez votre projet – et je vous donne une première impression honnête.",
      howTitle: "Comment ça se passe",
      howSteps: [
        "Choisissez votre date et heure préférées.",
        "Je confirme sous 24 h par e-mail.",
        "Nous nous voyons en visio (lien à suivre).",
        "Vous recevez ensuite une courte note.",
      ],
      goodToKnow: "Bon à savoir",
      goodPoints: [
        "Langue : allemand, anglais ou français",
        "Outil : Google Meet ou MS Teams",
        "Fuseau : CET / Europe/Berlin",
        "NDA possible en amont",
      ],
      fields: {
        name: "Nom",
        email: "E-mail",
        company: "Entreprise (optionnel)",
        phone: "Téléphone (optionnel)",
        topic: "De quoi s'agit-il ?",
        date: "Date souhaitée",
        time: "Heure",
        message: "Contexte / courte note (optionnel)",
      },
      topicDefault: "Entretien de découverte (30 min)",
      submit: "Demander un rendez-vous",
      sending: "Envoi...",
      success: "Demande reçue ! Je confirme votre créneau par e-mail – généralement sous 24 h.",
    },
    footer: {
      tagline: "Consultant IT freelance & développeur logiciel basé en Allemagne et au Portugal. 9+ ans d'expérience en backend engineering, assurance qualité et coordination de projets pour des clients enterprise.",
      navHeading: "Navigation",
      contactHeading: "Contact direct",
      cvLink: "CV (PDF) ↓",
      copyright: "© {year} Karim Benna · IT Consulting — Limitless Solutions.",
      imprint: "Mentions légales",
      privacy: "Confidentialité",
    },
    footerCallout: {
      ctaTitleA: "Donnons vie à votre prochaine",
      ctaAccent: "idée",
      ctaTitleEnd: ".",
      ctaDesc: "Si vous cherchez un consultant IT fiable qui réfléchit avec vous, prend ses responsabilités et livre – vous êtes au bon endroit.",
    },
    notFound: {
      title: "Page introuvable",
      back: "Retour à l'accueil",
    },
  },
};
